/**
 * The Plan an Order form: boxes to fill in, instead of a wall of text to read and answer.
 *
 * Greg, 2026-10-09: "drop-downs for yes/no questions, input fields, upload a PDF if
 * they've got an alternate quote... so all the information can get in and then you give
 * them recommendations. They can pick a choice and then from there order placed, convert
 * to PO."
 *
 * Two halves:
 *   - About the product and vendor: asked ONCE. Anything already saved shows as known
 *     and is not asked. What is typed here is saved to the card when the form is sent.
 *   - This order: asked every time.
 * Sending the form writes the buyer's first message of the chat (server:
 * lib/order-form.js), and the coach answers with options shown as cards (ChoiceCards).
 *
 * ⚠️ The form never works anything out. Totals on the option cards come from the server.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Paperclip } from 'lucide-react';
import { apBuyFacts, apInvoiceCoach } from '../services/api';

const CARD = [
  { kind: 'usage', label: 'How much we use', q: 'Does each sale use some of this up?', hint: 'e.g. One bag per dozen; a 50 count takes 4' },
  { kind: 'shelf_life', label: 'Shelf life', q: 'Does it have a shelf life?', hint: 'e.g. 10 days refrigerated' },
  { kind: 'storage', label: 'Storage space', q: 'Is there a most we can hold?', hint: 'e.g. Room for about 6,000' },
  { kind: 'freight', label: 'Freight', q: 'Is there a freight charge or minimum?', hint: 'e.g. Flat $25 an order; free over $500' },
  { kind: 'lead_time', label: 'Lead time', q: 'Does it take time to arrive?', hint: 'e.g. About a week' },
  { kind: 'min_order', label: 'Minimum order', q: 'Is there a minimum order or pack size?', hint: 'e.g. Case of 2,000; $150 minimum' },
  { kind: 'terms', label: 'Payment terms', q: 'Do they give payment terms?', hint: 'e.g. Net 30' },
];
const TERMS = ['', 'On delivery', 'Net 7', 'Net 10', 'Net 15', 'Net 30', 'Net 45', 'Net 60'];
const MAX_FILE = 4 * 1024 * 1024;

const lab = { fontSize: 11.5, fontWeight: 600, color: 'var(--gray-600, #4b5563)', marginBottom: 3 };
const row = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 };
const money = (v) => (v == null || !isFinite(Number(v)) ? '' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('Could not read that file.'));
  r.readAsDataURL(file);
});

const blankOrder = (title) => ({
  item: title || '', quantity: '', unit: '', unitPrice: '', onHand: '', needBy: '', termsThisOrder: '',
  hasTiers: '', tiers: [{ quantity: '', unitPrice: '' }],
  hasOtherQuote: '', otherQuotes: [{ vendor: '', unitPrice: '', note: '' }], notes: '',
});

export default function OrderForm({ planId, plan, working, onSent }) {
  const messages = plan.messages || [];
  const last = [...messages].reverse().find((m) => m && m.role === 'system' && m.kind === 'order_form');
  const started = messages.some((m) => m && m.role === 'user' && m.text);

  const [open, setOpen] = useState(!started);
  const [known, setKnown] = useState({});       // kind -> saved fact text
  const [card, setCard] = useState({});
  const [order, setOrder] = useState(() => (last && last.data && last.data.order
    ? { ...blankOrder(plan.title), ...last.data.order,
      quantity: last.data.order.quantity == null ? '' : String(last.data.order.quantity),
      unitPrice: last.data.order.unitPrice == null ? '' : String(last.data.order.unitPrice),
      onHand: last.data.order.onHand == null ? '' : String(last.data.order.onHand),
      tiers: (last.data.order.tiers || []).length ? last.data.order.tiers.map((t) => ({ quantity: String(t.quantity), unitPrice: String(t.unitPrice) })) : [{ quantity: '', unitPrice: '' }],
      otherQuotes: (last.data.order.otherQuotes || []).length ? last.data.order.otherQuotes.map((q) => ({ vendor: q.vendor || '', unitPrice: q.unitPrice == null ? '' : String(q.unitPrice), note: q.note || '' })) : [{ vendor: '', unitPrice: '', note: '' }] }
    : blankOrder(plan.title)));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // What is already on the card, so it is shown as known and not asked again.
  const loadKnown = useCallback(async () => {
    try {
      const d = await apBuyFacts('plan', { planId });
      const k = {};
      for (const f of d.relevant || []) if (!k[f.kind]) k[f.kind] = f.fact;
      setKnown(k);
    } catch (e) { /* the form still works; it just asks everything */ }
  }, [planId]);
  useEffect(() => { loadKnown(); }, [loadKnown, messages.length]);

  const setO = (patch) => setOrder((o) => ({ ...o, ...patch }));
  const setC = (kind, patch) => setCard((c) => ({ ...c, [kind]: { state: '', text: '', ...(c[kind] || {}), ...patch } }));
  const pickFile = (f) => {
    setErr('');
    if (!f) { setFile(null); return; }
    if (f.size > MAX_FILE) { setErr('That file is over 4MB. Send a smaller scan, or type the quote in.'); return; }
    setFile(f);
  };

  const total = order.quantity && order.unitPrice && isFinite(Number(order.quantity)) && isFinite(Number(order.unitPrice))
    ? Number(String(order.quantity).replace(/,/g, '')) * Number(String(order.unitPrice).replace(/[$,]/g, '')) : null;
  const asking = CARD.filter((c) => !known[c.kind]);

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const quote = file && order.hasOtherQuote === 'yes'
        ? { filename: file.name, mediaType: file.type, data: await fileToBase64(file) } : undefined;
      await apInvoiceCoach('submit-form', { planId, order, card, quote });
      setFile(null); setCard({}); setOpen(false);
      if (onSent) await onSent();
    } catch (e) { setErr(e.message || 'The form did not send.'); }
    setBusy(false);
  };

  if (!open) {
    return (
      <div style={{ margin: '0 0 10px' }}>
        <button className="btn btn-outline" style={{ fontSize: 12.5 }} onClick={() => setOpen(true)}>
          <ChevronRight size={13} /> {last ? 'Change The Order Form And Ask Again' : 'Fill In The Order Form'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, padding: 14, margin: '0 0 12px', background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy, #00395C)' }}>Order form</div>
        {started && (
          <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => setOpen(false)}>
            <ChevronDown size={13} /> Hide
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)', marginBottom: 6 }}>
        This order
      </div>
      <div style={row}>
        <div style={{ gridColumn: 'span 2' }}>
          <div style={lab}>What are you ordering?</div>
          <input className="form-input" style={{ width: '100%' }} value={order.item} onChange={(e) => setO({ item: e.target.value })} />
        </div>
        <div>
          <div style={lab}>How many?</div>
          <input className="form-input" style={{ width: '100%' }} inputMode="decimal" value={order.quantity} onChange={(e) => setO({ quantity: e.target.value })} />
        </div>
        <div>
          <div style={lab}>Counted in</div>
          <input className="form-input" style={{ width: '100%' }} placeholder="bags, lb, cases" value={order.unit} onChange={(e) => setO({ unit: e.target.value })} />
        </div>
      </div>
      <div style={row}>
        <div>
          <div style={lab}>Price quoted, each ($)</div>
          <input className="form-input" style={{ width: '100%' }} inputMode="decimal" value={order.unitPrice} onChange={(e) => setO({ unitPrice: e.target.value })} />
          {total != null && isFinite(total) && <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 2 }}>{money(total)} in all</div>}
        </div>
        <div>
          <div style={lab}>On the shelf now</div>
          <input className="form-input" style={{ width: '100%' }} inputMode="decimal" placeholder="a count, even a rough one" value={order.onHand} onChange={(e) => setO({ onHand: e.target.value })} />
          <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 2 }}>Kept as the starting count for next time</div>
        </div>
        <div>
          <div style={lab}>Needed by</div>
          <input className="form-input" style={{ width: '100%' }} type="date" min={todayLocal()} value={order.needBy} onChange={(e) => setO({ needBy: e.target.value })} />
        </div>
        <div>
          <div style={lab}>Terms on this order</div>
          <select className="form-input" style={{ width: '100%' }} value={order.termsThisOrder} onChange={(e) => setO({ termsThisOrder: e.target.value })}>
            {TERMS.map((t) => <option key={t} value={t}>{t || 'Not sure'}</option>)}
          </select>
        </div>
      </div>

      <div style={row}>
        <div>
          <div style={lab}>Does the price change with volume?</div>
          <select className="form-input" style={{ width: '100%' }} value={order.hasTiers} onChange={(e) => setO({ hasTiers: e.target.value })}>
            <option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option>
          </select>
        </div>
        <div>
          <div style={lab}>Did you get another quote?</div>
          <select className="form-input" style={{ width: '100%' }} value={order.hasOtherQuote} onChange={(e) => setO({ hasOtherQuote: e.target.value })}>
            <option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option>
          </select>
        </div>
      </div>

      {order.hasTiers === 'yes' && (
        <div style={{ marginBottom: 10 }}>
          <div style={lab}>Price at other quantities</div>
          {order.tiers.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <input className="form-input" style={{ width: 130 }} inputMode="decimal" placeholder="Quantity" value={t.quantity}
                onChange={(e) => setO({ tiers: order.tiers.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)) })} />
              <span style={{ fontSize: 12.5 }}>at</span>
              <input className="form-input" style={{ width: 130 }} inputMode="decimal" placeholder="$ each" value={t.unitPrice}
                onChange={(e) => setO({ tiers: order.tiers.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)) })} />
            </div>
          ))}
          {order.tiers.length < 4 && (
            <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => setO({ tiers: [...order.tiers, { quantity: '', unitPrice: '' }] })}>
              Add Another Quantity
            </button>
          )}
        </div>
      )}

      {order.hasOtherQuote === 'yes' && (
        <div style={{ marginBottom: 10 }}>
          <div style={lab}>The other quote</div>
          {order.otherQuotes.map((q, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <input className="form-input" style={{ flex: '1 1 160px' }} placeholder="Vendor" value={q.vendor}
                onChange={(e) => setO({ otherQuotes: order.otherQuotes.map((x, j) => (j === i ? { ...x, vendor: e.target.value } : x)) })} />
              <input className="form-input" style={{ width: 120 }} inputMode="decimal" placeholder="$ each" value={q.unitPrice}
                onChange={(e) => setO({ otherQuotes: order.otherQuotes.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)) })} />
              <input className="form-input" style={{ flex: '2 1 200px' }} placeholder="Anything else about it" value={q.note}
                onChange={(e) => setO({ otherQuotes: order.otherQuotes.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)) })} />
            </div>
          ))}
          <label className="btn btn-outline" style={{ fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Paperclip size={13} /> {file ? file.name : 'Attach The Quote (PDF or photo)'}
            <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
              onChange={(e) => pickFile(e.target.files && e.target.files[0])} />
          </label>
          {file && <button className="btn btn-ghost" style={{ marginLeft: 6 }} onClick={() => setFile(null)}>Remove</button>}
          <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)', marginTop: 3 }}>
            It is read for the vendor, price and terms, and kept with this plan.
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={lab}>Anything else the coach should know</div>
        <textarea className="form-input" rows={2} style={{ width: '100%', resize: 'vertical' }} value={order.notes}
          onChange={(e) => setO({ notes: e.target.value })} />
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)', marginBottom: 2 }}>
        About this product and vendor
      </div>
      <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', marginBottom: 8 }}>
        Asked once. Your answers are saved to the card and not asked again.
      </div>
      {CARD.filter((c) => known[c.kind]).map((c) => (
        <div key={c.kind} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0', alignItems: 'baseline' }}>
          <Check size={13} style={{ color: '#1c7c43', flexShrink: 0 }} />
          <span style={{ width: 130, flexShrink: 0, color: 'var(--gray-600, #4b5563)' }}>{c.label}</span>
          <span>{known[c.kind]}</span>
        </div>
      ))}
      {asking.map((c) => {
        const a = card[c.kind] || { state: '', text: '' };
        return (
          <div key={c.kind} style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ flex: '1 1 220px', fontSize: 13 }}>{c.q}</span>
            <select className="form-input" style={{ width: 130 }} value={a.state} onChange={(e) => setC(c.kind, { state: e.target.value })}>
              <option value="">Choose…</option><option value="yes">Yes</option><option value="none">None</option><option value="unknown">Don't know</option>
            </select>
            {a.state === 'yes' && (
              <input className="form-input" style={{ flex: '2 1 240px' }} placeholder={c.hint} value={a.text}
                onChange={(e) => setC(c.kind, { text: e.target.value })} />
            )}
          </div>
        );
      })}

      {err && <div style={{ color: '#b42318', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={busy || working || !order.item.trim() || !String(order.quantity).trim()} onClick={send}>
          {busy ? (file ? 'Reading the quote…' : 'Sending…') : 'Get My Options'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
          Fill in what you know. Anything left on Choose… is simply left out.
        </span>
      </div>
    </div>
  );
}

// The coach's options, as cards to pick from.
export function ChoiceCards({ choices, onChoose, disabled }) {
  if (!choices || !Array.isArray(choices.options) || !choices.options.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 10 }}>
      {choices.options.map((o, i) => {
        const rec = choices.recommended === i + 1;
        return (
          <div key={i} style={{
            border: `1px solid ${rec ? 'var(--orange, #DC582A)' : 'var(--gray-200, #e5e7eb)'}`,
            borderTop: `4px solid ${rec ? 'var(--orange, #DC582A)' : 'var(--navy, #00395C)'}`,
            borderRadius: 8, padding: 12, background: '#fff', display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            {rec && <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--orange, #DC582A)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Recommended</div>}
            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--navy, #00395C)' }}>{o.label}</div>
            {(o.quantity != null || o.vendor) && (
              <div style={{ fontSize: 12.5, color: 'var(--gray-600, #4b5563)' }}>
                {o.quantity != null ? `${Number(o.quantity).toLocaleString('en-US')}${o.unit ? ` ${o.unit}` : ''}` : ''}
                {o.unitPrice != null ? ` at $${Number(o.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : ''}
                {o.vendor ? `${o.quantity != null ? ' · ' : ''}${o.vendor}` : ''}
              </div>
            )}
            {o.total != null && (
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {money(o.total)}
                {o.extra != null && <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--gray-500, #6b7280)' }}> incl. {money(o.extra)} {o.extraLabel}</span>}
              </div>
            )}
            <div style={{ fontSize: 13, lineHeight: 1.45 }}>{o.why}</div>
            {o.watchOut && <div style={{ fontSize: 12.5, color: '#9a4a1c', lineHeight: 1.4 }}>Check: {o.watchOut}</div>}
            {onChoose && (
              <button className={rec ? 'btn btn-primary' : 'btn btn-outline'} style={{ marginTop: 'auto', fontSize: 12.5 }}
                disabled={disabled} onClick={() => onChoose(o)}>
                Choose This
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
